import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Paperclip, X, Upload, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { qmbAPI } from '../../lib/api';

const empty = {
  title: '', content: '', category: 'info', is_pinned: false,
  require_confirmation: false, target_all: false, target_departments: [], attachments: [],
};

export const PostEditorDialog = ({ open, onOpenChange, meta, post, onSaved }) => {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    if (!form.target_all && form.target_departments.length === 0) { setPreview(null); return undefined; }
    let active = true;
    setPreviewing(true);
    const t = setTimeout(async () => {
      try {
        const res = await qmbAPI.audiencePreview({ target_all: form.target_all, target_departments: form.target_departments });
        if (active) setPreview(res.data);
      } catch {
        if (active) setPreview(null);
      } finally {
        if (active) setPreviewing(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [open, form.target_all, form.target_departments]);

  useEffect(() => {
    if (open) {
      setForm(post ? {
        title: post.title || '', content: post.content || '', category: post.category || 'info',
        is_pinned: !!post.is_pinned, require_confirmation: !!post.require_confirmation,
        target_all: !!post.target_all, target_departments: post.target_departments || [],
        attachments: post.attachments || [],
      } : empty);
    }
  }, [open, post]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleDepartment = (dep) => setForm((f) => ({
    ...f,
    target_departments: f.target_departments.includes(dep)
      ? f.target_departments.filter((x) => x !== dep)
      : [...f.target_departments, dep],
  }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await qmbAPI.upload(file);
      setForm((f) => ({ ...f, attachments: [...f.attachments, res.data] }));
      toast.success('Datei hochgeladen');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeAttachment = (id) => setForm((f) => ({ ...f, attachments: f.attachments.filter((a) => a.id !== id) }));

  const save = async () => {
    if (!form.title.trim()) { toast.error('Titel erforderlich'); return; }
    if (!form.target_all && form.target_departments.length === 0) {
      toast.error('Bitte Zielgruppe wählen (alle oder mind. eine Maßnahme/Abteilung)');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title, content: form.content, category: form.category,
        is_pinned: form.is_pinned, require_confirmation: form.require_confirmation,
        target_all: form.target_all, target_departments: form.target_all ? [] : form.target_departments,
        attachment_ids: form.attachments.map((a) => a.id),
      };
      if (post) {
        await qmbAPI.updatePost(post.id, payload);
        toast.success('QMB-Info aktualisiert');
      } else {
        const res = await qmbAPI.createPost(payload);
        const n = res.data?.notified || 0;
        toast.success(`QMB-Info veröffentlicht${n ? ` · ${n} Person(en) benachrichtigt` : ''}`);
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="qmb-editor-dialog">
        <DialogHeader>
          <DialogTitle>{post ? 'QMB-Info bearbeiten' : 'Neue QMB-Info'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Titel *</Label>
            <Input value={form.title} onChange={(e) => set('title', e.target.value)}
              placeholder="z. B. Verfahrensanweisung Datenschutz V2" data-testid="qmb-title-input" />
          </div>
          <div>
            <Label>Inhalt</Label>
            <Textarea value={form.content} onChange={(e) => set('content', e.target.value)}
              rows={5} placeholder="Beschreibung / Hinweistext…" data-testid="qmb-content-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Kategorie</Label>
              <Select value={form.category} onValueChange={(v) => set('category', v)}>
                <SelectTrigger data-testid="qmb-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta?.categories || []).map((c) => (
                    <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-6 pt-6">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch checked={form.is_pinned} onCheckedChange={(v) => set('is_pinned', v)} data-testid="qmb-pin-switch" />
                Anpinnen
              </label>
            </div>
          </div>

          <div className="rounded-lg border p-3 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Zielgruppe (Maßnahme / Abteilung)</Label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch checked={form.target_all} onCheckedChange={(v) => set('target_all', v)} data-testid="qmb-target-all-switch" />
                Alle Mitarbeiter
              </label>
            </div>
            {!form.target_all && (
              <ScrollArea className="h-40 rounded border bg-secondary/20 p-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {(meta?.departments || []).map((dep) => (
                    <label key={dep.value} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded hover:bg-secondary/50 cursor-pointer"
                      data-testid={`qmb-department-${dep.value}`}>
                      <Checkbox checked={form.target_departments.includes(dep.value)} onCheckedChange={() => toggleDepartment(dep.value)} />
                      <span className="truncate flex-1">{dep.value}</span>
                      <span className="text-xs text-muted-foreground shrink-0">{dep.count} MA</span>
                    </label>
                  ))}
                  {(meta?.departments || []).length === 0 && (
                    <p className="text-xs text-muted-foreground p-2">Keine Maßnahmen/Abteilungen bei Mitarbeitern hinterlegt.</p>
                  )}
                </div>
              </ScrollArea>
            )}
            {(form.target_all || form.target_departments.length > 0) && (
              <div className="text-sm rounded-md bg-secondary/40 px-3 py-2 flex items-center gap-2" data-testid="qmb-audience-preview">
                <Users className="h-4 w-4 text-primary shrink-0" />
                {previewing ? (
                  <span className="text-muted-foreground">Berechne Zielgruppe…</span>
                ) : preview ? (
                  <span>
                    Zielgruppe: <strong>{preview.total}</strong> Mitarbeiter
                    {preview.without_login > 0 && (
                      <span className="text-amber-700">
                        {' · '}{preview.without_login} ohne Login
                        {form.require_confirmation ? ' (sehen die Info nicht)' : ''}
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </div>
            )}
          </div>

          <label className="flex items-start gap-2 text-sm cursor-pointer rounded-lg border p-3">
            <Switch checked={form.require_confirmation} onCheckedChange={(v) => set('require_confirmation', v)}
              data-testid="qmb-confirm-switch" className="mt-0.5" />
            <span>
              <span className="font-medium">Lesebestätigung erforderlich</span>
              <span className="block text-xs text-muted-foreground">Zielgruppe muss den Erhalt bestätigen (Audit-Nachweis + Excel-Export).</span>
            </span>
          </label>

          <div className="space-y-2">
            <Label>Anhänge</Label>
            <div className="flex flex-wrap gap-2">
              {form.attachments.map((a) => (
                <Badge key={a.id} variant="outline" className="gap-1 py-1">
                  <Paperclip className="h-3 w-3" /> {a.original_filename}
                  <button type="button" onClick={() => removeAttachment(a.id)} className="ml-1 hover:text-red-600">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <input ref={fileRef} type="file" className="hidden" onChange={handleUpload}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv" data-testid="qmb-file-input" />
            <Button type="button" variant="outline" size="sm" disabled={uploading}
              onClick={() => fileRef.current?.click()} data-testid="qmb-upload-btn">
              {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
              Datei anhängen
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={save} disabled={saving} data-testid="qmb-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            {post ? 'Speichern' : 'Veröffentlichen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
